/**
 * Field-app role store.
 *
 * The field app serves three personas off ONE shared dispatch-board model:
 *
 *   - `worker`      — a crew member: sees their crew's activities, records
 *                     time, completes the safety check, marks task progress.
 *   - `supervisor`  — the crew LEAD for the day (`crew.leadUserId`). Backend
 *                     has no separate Supervisor role — "supervisor" IS the
 *                     designated lead. Lead-only: end-of-day capture,
 *                     roll-over, manage who's present.
 *   - `compliance`  — an inspector on an `isCompliance` activity: runs the
 *                     checklist per scaffold, completes the visit, schedules
 *                     the next weekly visit.
 *
 * In the PROTOTYPE there is no backend to derive the live role from (a real
 * token gets logged out on app-init), so the active persona is a manual,
 * localStorage-persisted choice driven by the in-app role switcher. When the
 * backend lands, `deriveAvailableRoles()` (see use-field-role) computes which
 * lenses the user may actually use and the switcher only offers those.
 */
export type FieldRole = 'worker' | 'supervisor' | 'compliance';

export const FIELD_ROLES: FieldRole[] = ['worker', 'supervisor', 'compliance'];

const STORAGE_KEY = 'fieldtrix_active_role';
const DEFAULT_ROLE: FieldRole = 'worker';

function isFieldRole(value: unknown): value is FieldRole {
  return value === 'worker' || value === 'supervisor' || value === 'compliance';
}

function readInitial(): FieldRole {
  if (typeof window === 'undefined') return DEFAULT_ROLE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isFieldRole(stored) ? stored : DEFAULT_ROLE;
  } catch {
    return DEFAULT_ROLE;
  }
}

let currentRole: FieldRole = readInitial();
const listeners = new Set<() => void>();

/** Read the active persona (synchronous — for `useSyncExternalStore`). */
export function getActiveRole(): FieldRole {
  return currentRole;
}

/** Switch the active persona and persist it. No-op when unchanged. */
export function setActiveRole(role: FieldRole): void {
  if (role === currentRole) return;
  currentRole = role;
  try {
    window.localStorage.setItem(STORAGE_KEY, role);
  } catch {
    /* storage unavailable — keep the in-memory value */
  }
  listeners.forEach((fn) => fn());
}

/** Subscribe to role changes (for `useSyncExternalStore`). */
export function subscribeRole(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
